import type { Socket } from "socket.io-client";

export const useSocketChat = () => {
  // ВАЖНО: НЕ вызываем useNuxtApp() и useRuntimeConfig() на верхнем уровне
  // Это вызовет ошибку на SSR если composable вызывается из app.vue
  const socket = ref<Socket | null>(null);
  const supportSocket = ref<Socket | null>(null);
  const isTyping = ref<boolean>(false);
  const isStartTyping = ref<boolean>(false);
  const isListeningMessages = ref<boolean>(false);
  const isListeningUserTyping = ref<boolean>(false);
  const isListeningUserStopTyping = ref<boolean>(false);
  const timerTyping = ref<any>(null);

  const connect = (token: string, type: string = "chat") => {
    const { $io } = useNuxtApp() as any;
    if (!$io) return;
    const config = useRuntimeConfig();
    const socketConfig = {
      auth: { token },
      extraHeaders: { Authorization: `Bearer ${token}` },
    };
    if (type === "support") {
      supportSocket.value = $io(
        `${config.public.SOCKET_API_URL}/ticket`,
        socketConfig
      );
    } else {
      socket.value = $io(
        `${config.public.SOCKET_API_URL}/${type}`,
        socketConfig
      );
    }
  };

  const disconnect = (chatId: number) => {
    if (socket.value) {
      if (chatId) leaveChat(chatId);
      socket.value.disconnect();
      socket.value = null;
      isListeningMessages.value = false;
      isListeningUserTyping.value = false;
      isListeningUserStopTyping.value = false;
    }
    if (supportSocket.value) {
      supportSocket.value.disconnect();
      supportSocket.value = null;
    }
  };

  const joinChat = (chatId: number) => {
    if (!socket.value) return;
    socket.value.emit("joinChat", { chatId });
  };

  const joinTicket = (ticketId: number) => {
    if (!supportSocket.value) return;
    supportSocket.value.emit("joinTicket", { ticketId });
  };

  const leaveChat = (chatId: number) => {
    if (!socket.value) return;
    socket.value.emit("leaveChat", { chatId });
    // Сброс слушателей, добавил
    isListeningMessages.value = false;
    isListeningUserTyping.value = false;
    isListeningUserStopTyping.value = false;
  };

  const leaveTicket = (ticketId: number) => {
    if (!supportSocket.value) return;
    supportSocket.value.emit("leaveTicket", { ticketId });
    // Сброс слушателей, добавил
    isListeningMessages.value = false;
    isListeningUserTyping.value = false;
    isListeningUserStopTyping.value = false;
  };

  const startTyping = (id: number, type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s) return;
    isStartTyping.value = true;
    s.emit("startTyping", type === "chat" ? { chatId: id } : { ticketId: id });
  };

  const stopTyping = (id: number, type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s) return;
    isStartTyping.value = false;
    s.emit("stopTyping", type === "chat" ? { chatId: id } : { ticketId: id });
  };

  const handleTyping = (currentUserId: number, type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s) return;

    s.on("userTyping", (data: any) => {
      if (data.userId === currentUserId) return;
      isListeningUserTyping.value = true;
      isTyping.value = data.isTyping;
    });

    s.on("userStopTyping", () => {
      if (isListeningUserStopTyping.value) return;
      isListeningUserStopTyping.value = true;
      isTyping.value = false;
    });
  };

  const handleNewMessage = (callback: (data: any) => void, type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s || isListeningMessages.value) return;
    isListeningMessages.value = true;

    s.off("syncMessage");
    s.on("syncMessage", (data: any) => {
      callback(data);
    });
  };

  const handleUserJoined = (callback: (data: any) => void, type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s) return;
    s.on("userJoined", (data: any) => {
      callback(data);
    });
  };

  const handleChatClose = (callback: (data: any) => void, type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s) return;
    s.on("ticketClosed", (data: any) => {
      callback(data);
    });
  };

  const setupTypingTimer = (id: number, type: string) => {
    if (!isStartTyping.value) startTyping(id, type);
    clearTimeout(timerTyping.value);
    timerTyping.value = setTimeout(() => {
      stopTyping(id, type);
    }, 1500);
  };

  const closeTicket = (ticketId: number) => {
    if (!supportSocket.value) return;
    supportSocket.value.emit("closeTicket", { ticketId });
  };

  const off = (type: string) => {
    const s = type === "chat" ? socket.value : supportSocket.value;
    if (!s) return;
    s.off("userTyping");
    s.off("userStopTyping");
    s.off("syncMessage");
    s.off("ticketClosed");
  };

  return {
    socket,
    supportSocket,
    isTyping,
    isListeningMessages,
    isListeningUserTyping,
    isListeningUserStopTyping,
    connect,
    disconnect,
    joinChat,
    joinTicket,
    leaveChat,
    leaveTicket,
    setupTypingTimer,
    stopTyping,
    handleTyping,
    handleNewMessage,
    handleChatClose,
    handleUserJoined,
    off,
  };
};
